import { useQuery } from '@tanstack/react-query'
import forge from 'node-forge'

import { QUERY_KEYS } from '../tanstack-react-query/queryKeys'
import { env } from '@/config/env'

/**
 * Generates a secure session ID for RAIN card provider
 * Uses RSA-OAEP encryption with SHA-1 for encrypting a random secret key
 *
 * Flow:
 * 1. Generate 16 random bytes as secretKey
 * 2. Convert secretKey to base64
 * 3. Encrypt base64 secretKey using RSA public key
 * 4. Return both secretKey (for local decryption) and sessionId (for API)
 */
export async function generateSessionId(): Promise<{
  secretKey: string
  sessionId: string
}> {
  const pem = env.RAIN_PUBLIC_KEY

  if (!pem) {
    throw new Error('RAIN public key is not configured')
  }

  // Generate 16 random bytes as secret key
  const randomBytes = forge.random.getBytesSync(16)
  const secretKey = forge.util.bytesToHex(randomBytes)

  // Convert secret key to base64
  const secretKeyBuffer = Buffer.from(secretKey, 'hex')
  const secretKeyBase64 = secretKeyBuffer.toString('base64')

  // Prepare data for encryption
  const dataToEncrypt = Buffer.from(secretKeyBase64, 'utf-8').toString('binary')

  // Parse RSA public key from PEM
  const publicKey = forge.pki.publicKeyFromPem(pem)

  // Encrypt using RSA-OAEP with SHA-1
  const encryptedBytes = publicKey.encrypt(dataToEncrypt, 'RSA-OAEP', {
    md: forge.md.sha1.create(),
    mgf1: {
      md: forge.md.sha1.create(),
    },
  })

  // Encode encrypted bytes to base64 for transmission
  const sessionId = forge.util.encode64(encryptedBytes)

  return {
    secretKey,
    sessionId,
  }
}

/**
 * React Query hook for generating and caching RAIN session ID
 * Session is cached for 5 minutes to minimize key generation overhead
 *
 * Note: Returns null data if RAIN public key is not configured,
 * allowing fallback to legacy card info flow
 */
export const useGenerateSessionId = () => {
  const pem = env.RAIN_PUBLIC_KEY

  return useQuery({
    queryKey: [QUERY_KEYS.RAIN_SESSION_ID],
    queryFn: () => generateSessionId(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    refetchOnWindowFocus: false,
    retry: 2,
    // Only enable if RAIN public key is configured
    enabled: !!pem,
  })
}
